import React, { useEffect, useState } from 'react';
import { Card, Badge } from '@/components/ui';
import {
  adminStatsService,
  RevenueData,
  TenantRevenueBreakdown,
} from '@/services/adminStats.service';

interface RevenueChartProps {
  className?: string;
}

// Bar Chart Component
interface BarChartProps {
  data: RevenueData[];
  isLoading?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({ data, isLoading }) => {
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="h-56 flex items-end space-x-2 px-2">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="flex-1 flex flex-col items-center">
            <div
              className="w-full bg-secondary-200 rounded-t animate-pulse"
              style={{ height: `${Math.random() * 60 + 20}%` }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-56 flex items-center justify-center">
        <p className="text-secondary-500">No revenue data available</p>
      </div>
    );
  }

  const maxRevenue = Math.max(...data.map(d => d.totalRevenue), 1);

  return (
    <div>
      {/* Chart Legend */}
      <div className="flex items-center justify-end space-x-4 mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded bg-primary-500" />
          <span className="text-xs text-secondary-600">QLead Revenue</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded bg-success-500" />
          <span className="text-xs text-secondary-600">Subscription Revenue</span>
        </div>
      </div>

      {/* Stacked Bar Chart */}
      <div className="h-56 flex items-end space-x-1 px-1 relative">
        {data.map((d, i) => {
          const qLeadHeight = (d.qLeadRevenue / maxRevenue) * 100;
          const subscriptionHeight = (d.subscriptionRevenue / maxRevenue) * 100;
          const isHovered = hoveredBar === i;

          return (
            <div
              key={i}
              className="flex-1 flex flex-col items-center relative cursor-pointer group"
              onMouseEnter={() => setHoveredBar(i)}
              onMouseLeave={() => setHoveredBar(null)}
            >
              {/* Tooltip */}
              {isHovered && (
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 z-10 bg-secondary-900 text-white text-xs rounded-lg p-2 whitespace-nowrap shadow-lg">
                  <p className="font-semibold">{d.month} {d.year}</p>
                  <p className="text-primary-300">QLead: ${d.qLeadRevenue.toLocaleString()}</p>
                  <p className="text-success-300">Subscription: ${d.subscriptionRevenue.toLocaleString()}</p>
                  <p className="border-t border-secondary-700 mt-1 pt-1">
                    Total: ${d.totalRevenue.toLocaleString()}
                  </p>
                </div>
              )}

              {/* Stacked Bars */}
              <div className="w-full flex flex-col items-stretch">
                {/* Subscription bar (top) */}
                <div
                  className={`w-full bg-success-500 rounded-t transition-all duration-200 ${
                    isHovered ? 'bg-success-600' : ''
                  }`}
                  style={{ height: `${subscriptionHeight * 2}px` }}
                />
                {/* QLead bar (bottom) */}
                <div
                  className={`w-full bg-primary-500 transition-all duration-200 ${
                    isHovered ? 'bg-primary-600' : ''
                  }`}
                  style={{ height: `${qLeadHeight * 2}px` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between text-xs text-secondary-500 mt-2 px-1">
        {data.map((d, i) => (
          <span key={i} className="text-center flex-1 truncate">
            {d.month}
          </span>
        ))}
      </div>
    </div>
  );
};

// Donut Chart for Revenue Breakdown
interface DonutChartProps {
  data: TenantRevenueBreakdown[];
  isLoading?: boolean;
}

const DonutChart: React.FC<DonutChartProps> = ({ data, isLoading }) => {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-32 h-32 rounded-full border-8 border-secondary-200 animate-pulse" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-secondary-500">No data available</p>
      </div>
    );
  }

  const colors = [
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // yellow
    '#ef4444', // red
    '#8b5cf6', // purple
    '#06b6d4', // cyan
    '#f97316', // orange
    '#6b7280', // gray
  ];

  const totalRevenue = data.reduce((sum, d) => sum + d.revenue, 0);
  let currentAngle = 0;

  const segments = data.map((d, i) => {
    const percentage = (d.revenue / totalRevenue) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    currentAngle += angle;

    return {
      ...d,
      color: colors[i % colors.length],
      startAngle,
      angle,
      percentage,
    };
  });

  const size = 120;
  const center = size / 2;
  const radius = 50;
  const innerRadius = 30;

  const getPath = (startAngle: number, angle: number, r: number, ir: number) => {
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((startAngle + angle - 90) * Math.PI) / 180;

    const x1 = center + r * Math.cos(startRad);
    const y1 = center + r * Math.sin(startRad);
    const x2 = center + r * Math.cos(endRad);
    const y2 = center + r * Math.sin(endRad);

    const x3 = center + ir * Math.cos(endRad);
    const y3 = center + ir * Math.sin(endRad);
    const x4 = center + ir * Math.cos(startRad);
    const y4 = center + ir * Math.sin(startRad);

    const largeArc = angle > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${ir} ${ir} 0 ${largeArc} 0 ${x4} ${y4} Z`;
  };

  return (
    <div className="flex items-center justify-between">
      {/* Donut Chart */}
      <div className="relative">
        <svg width={size} height={size}>
          {segments.map((segment, i) => (
            <path
              key={i}
              d={getPath(segment.startAngle, segment.angle, radius, innerRadius)}
              fill={segment.color}
              className="transition-all duration-200 cursor-pointer"
              style={{
                transform: hoveredSegment === i ? 'scale(1.05)' : 'scale(1)',
                transformOrigin: 'center',
              }}
              onMouseEnter={() => setHoveredSegment(i)}
              onMouseLeave={() => setHoveredSegment(null)}
            />
          ))}
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-bold text-secondary-900">
              ${(totalRevenue / 1000).toFixed(0)}k
            </p>
            <p className="text-xs text-secondary-500">Total</p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex-1 ml-6 space-y-2 max-h-48 overflow-y-auto">
        {segments.slice(0, 6).map((segment, i) => (
          <div
            key={i}
            className={`flex items-center justify-between p-2 rounded-lg transition-colors cursor-pointer ${
              hoveredSegment === i ? 'bg-secondary-50' : ''
            }`}
            onMouseEnter={() => setHoveredSegment(i)}
            onMouseLeave={() => setHoveredSegment(null)}
          >
            <div className="flex items-center space-x-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: segment.color }}
              />
              <span className="text-xs text-secondary-700 truncate max-w-24">
                {segment.tenantName}
              </span>
            </div>
            <span className="text-xs font-medium text-secondary-900">
              {segment.percentage.toFixed(0)}%
            </span>
          </div>
        ))}
        {segments.length > 6 && (
          <p className="text-xs text-secondary-500 text-center">
            +{segments.length - 6} more
          </p>
        )}
      </div>
    </div>
  );
};

export const RevenueChart: React.FC<RevenueChartProps> = ({ className = '' }) => {
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [breakdownData, setBreakdownData] = useState<TenantRevenueBreakdown[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'trend' | 'breakdown'>('trend');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [revenue, breakdown] = await Promise.all([
          adminStatsService.getRevenueAnalytics(),
          adminStatsService.getRevenueByTenant(),
        ]);
        setRevenueData(revenue);
        setBreakdownData(breakdown);
      } catch (err) {
        setError('Failed to load revenue data');
        console.error('Error fetching revenue data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalRevenue = revenueData.reduce((sum, d) => sum + d.totalRevenue, 0);
  const totalQLeadRevenue = revenueData.reduce((sum, d) => sum + d.qLeadRevenue, 0);
  const totalSubscriptionRevenue = revenueData.reduce((sum, d) => sum + d.subscriptionRevenue, 0);

  if (error) {
    return (
      <Card className={className}>
        <Card.Header>
          <h2 className="text-lg font-semibold text-secondary-900">Revenue Analytics</h2>
        </Card.Header>
        <Card.Body>
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-danger-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="mt-2 text-sm text-secondary-600">{error}</p>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Revenue Analytics</h2>
            <p className="text-sm text-secondary-500">QLead fees and subscription revenue</p>
          </div>
          <div className="flex items-center space-x-1 bg-secondary-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('trend')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'trend'
                  ? 'bg-white text-secondary-900 shadow-sm'
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              Trend
            </button>
            <button
              onClick={() => setActiveTab('breakdown')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'breakdown'
                  ? 'bg-white text-secondary-900 shadow-sm'
                  : 'text-secondary-600 hover:text-secondary-900'
              }`}
            >
              By Tenant
            </button>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {activeTab === 'trend' ? (
          <>
            <BarChart data={revenueData} isLoading={isLoading} />

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-secondary-100">
              <div className="text-center">
                <p className="text-xl font-bold text-secondary-900">
                  ${totalRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-secondary-500">Total Revenue</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-primary-600">
                  ${totalQLeadRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-secondary-500">QLead Revenue</p>
                <Badge variant="info" size="sm" className="mt-1">
                  {totalRevenue > 0
                    ? ((totalQLeadRevenue / totalRevenue) * 100).toFixed(0)
                    : 0}%
                </Badge>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-success-600">
                  ${totalSubscriptionRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-secondary-500">Subscription Revenue</p>
                <Badge variant="success" size="sm" className="mt-1">
                  {totalRevenue > 0
                    ? ((totalSubscriptionRevenue / totalRevenue) * 100).toFixed(0)
                    : 0}%
                </Badge>
              </div>
            </div>
          </>
        ) : (
          <DonutChart data={breakdownData} isLoading={isLoading} />
        )}
      </Card.Body>
    </Card>
  );
};

export default RevenueChart;
